/**
 * Available sizes
 * Each value should have a corresponding class in avatar.module.css
 */
export const AVATAR_SIZES = ['x-large', 'large', 'medium', 'small', 'x-small', 'xx-small'] as const;
export type AvatarSize = (typeof AVATAR_SIZES)[number];

/**
 * Corner icon overlay dimensions (bottom-right of Avatar).
 * Each value should have a corresponding class in avatar.module.css
 */
export const AVATAR_ICON_SIZES = ['16', '20', '24'] as const;
export type AvatarIconSize = (typeof AVATAR_ICON_SIZES)[number];

/**
 * Available status variants for the badge `type="status"` overlay.
 * Each value maps to a fixed icon and background colour — see `StatusIcon`.
 *
 * These are re-exported aliases of `STATUS_ICON_STATUSES` / `StatusIconStatus`
 * for use within the Avatar component tree.
 */
export { STATUS_ICON_STATUSES as AVATAR_BADGE_STATUSES } from '../status-icon/status-icon';
export type { StatusIconStatus as AvatarBadgeStatus } from '../status-icon/status-icon';

/** Pixel width for each semantic size token. */
export const AVATAR_SIZE_PX: Record<AvatarSize, number> = {
  'xx-small': 16,
  'x-small': 24,
  small: 32,
  medium: 40,
  large: 48,
  'x-large': 64,
};

/**
 * Corner icon size for a given avatar size (spec-driven, not configurable on `Avatar`).
 * - xx-small / x-small: no overlay (`null`).
 * - small: 16px icon.
 * - medium and above: 20px icon.
 *
 * The return type is `'16' | '20' | null` — the implementation never returns `'24'`
 * even though `AvatarIconSize` includes it (legacy overlay sizes).
 */
export function getIconSizeForAvatarSize(avatarSize: AvatarSize): '16' | '20' | null {
  const px = AVATAR_SIZE_PX[avatarSize];
  if (px <= 24) return null;
  if (px === 32) return '16';
  return '20';
}

// ── Initials display helpers ──────────────────────────────────────────────────

/** Sizes at which initials are capped to 1 character. */
export const SINGLE_INITIAL_SIZES = new Set<AvatarSize>(['xx-small', 'x-small']);

/** Returns the 1- or 2-character uppercase initials to display for the given size. */
export function getDisplayInitials(initials: string, size: AvatarSize): string {
  return initials.slice(0, SINGLE_INITIAL_SIZES.has(size) ? 1 : 2).toUpperCase();
}
